import _extends from '@babel/runtime-corejs3/helpers/extends';
import React__default, { useState, useMemo, useCallback } from 'react';
import classNames from 'classnames';
import styles from './Input2.js';
import { useInputGroupContext } from '../InputGroup/InputGroupProvider.js';
import { useFormFieldContext } from '../FormField/FormFieldContext.js';
import '../../utils/index.js';
import concatIds from '../../utils/concatIds/concatIds.js';

const Input = /*#__PURE__*/React__default.forwardRef((_ref, ref) => {
  let {
    className,
    value,
    defaultValue,
    disabled = false,
    full = false,
    invalid = false,
    multiline,
    onChange,
    onBlur,
    rows = 2,
    small = false,
    type = 'text',
    'aria-describedby': externalAriaDescribedBy,
    readOnly,
    onCompositionStart,
    onCompositionEnd,
    allowDecimalNumbers = true,
    min,
    max,
    ...rest
  } = _ref;
  const inputGroupContext = useInputGroupContext();
  const [compositionLock, setCompositionLock] = useState(false);
  const [inputValue, setInputValue] = useState(defaultValue !== null && defaultValue !== void 0 ? defaultValue : '');
  const inputRegex = useMemo(() => /^(?!-?\d+$).+$/, []);
  const minValue = useMemo(() => min !== undefined && min !== null ? Number(min) : undefined, [min]);
  const maxValue = useMemo(() => max !== undefined && max !== null ? Number(max) : undefined, [max]);
  const onBlurHandler = useCallback(event => {
    if (!allowDecimalNumbers && event.target.value !== '') {
      if (event.target.value === '-') {
        setInputValue('');
      } else {
        const currentValue = Number(event.target.value);
        if (minValue !== undefined && minValue > currentValue) {
          setInputValue(minValue);
        } else if (maxValue !== undefined && maxValue < currentValue) {
          setInputValue(maxValue);
        } else {
          setInputValue(currentValue);
        }
      }
    }
    onBlur === null || onBlur === void 0 || onBlur(event);
  }, [allowDecimalNumbers, maxValue, minValue, onBlur]);
  const onChangeHandler = useCallback(event => {
    if (!allowDecimalNumbers) {
      if (event.target.value === '' || event.target.value === '-' || !inputRegex.test(event.target.value)) {
        setInputValue(event.target.value);
        onChange === null || onChange === void 0 || onChange(event);
      }
    } else {
      setInputValue(event.target.value);
      if (!compositionLock) {
        onChange === null || onChange === void 0 || onChange(event);
      }
    }
  }, [allowDecimalNumbers, inputRegex, onChange, compositionLock]);
  const onCompositionHandlerStart = useCallback(event => {
    setInputValue(event.currentTarget.value);
    setCompositionLock(true);
    onCompositionStart === null || onCompositionStart === void 0 || onCompositionStart(event);
  }, [onCompositionStart]);
  const onCompositionHandlerEnd = useCallback(event => {
    setCompositionLock(false);
    onCompositionEnd === null || onCompositionEnd === void 0 || onCompositionEnd(event);
    onChange === null || onChange === void 0 || onChange(event);
    if (value !== undefined) {
      setInputValue('');
    }
  }, [onCompositionEnd, onChange, value]);
  const {
    helperTextId: formFieldHelperTextId,
    errorTextId: formFieldErrorTextId,
    isReadOnly: formFieldIsReadOnly
  } = useFormFieldContext();
  const internalAriaDescribedby = concatIds(externalAriaDescribedBy, formFieldHelperTextId, formFieldErrorTextId);
  const classes = classNames(styles.input, inputGroupContext.className, inputGroupContext.inputClassName, {
    [styles.full]: inputGroupContext.applyFull(full),
    [styles.invalid]: invalid,
    [styles.small]: inputGroupContext.applySmall(small),
    [styles.multiline]: multiline
  }, className);
  const commonProps = {
    ...rest,
    ref,
    className: classes,
    disabled,
    'aria-invalid': invalid,
    onBlur: onBlurHandler,
    onChange: value && !onChange || disabled ? null : onChangeHandler,
    onCompositionStart: onCompositionHandlerStart,
    onCompositionEnd: onCompositionHandlerEnd,
    value: compositionLock ? inputValue : value !== null && value !== void 0 ? value : inputValue,
    'aria-describedby': internalAriaDescribedby,
    readOnly: readOnly !== null && readOnly !== void 0 ? readOnly : formFieldIsReadOnly
  };
  return multiline ? /*#__PURE__*/React__default.createElement("textarea", _extends({}, commonProps, {
    rows: rows
  })) : /*#__PURE__*/React__default.createElement("input", _extends({}, commonProps, {
    type: allowDecimalNumbers ? type : 'text'
  }));
});

export { Input as default };
